/* melusio-lookbook — turns the lookbook's pages (sections/melusio-lookbook.liquid).
   Plan: docs/plans/2026-09-25-lookbook-book.md.

   The page is already a complete, still paper book before this runs. When GSAP and
   ScrollTrigger loaded and the visitor allows motion, this binds the pages into leaves:

     desktop (landscape, 990px+)  `m-look--spread`: an open book pinned under the header.
                                   Leaf 0 is the cover with the title's left page on its
                                   back; leaf k has spread k-1's right page in front and
                                   spread k's left page behind. Scrolling turns one leaf per
                                   step, snapping to whole turns. The closed book sits half a
                                   page left so the cover is centred, and slides back as the
                                   cover opens.
     phone or portrait            `m-look--single`: one page, turned off to the left by a
                                   swipe, the arrow buttons or the arrow keys. Vertical
                                   scrolling still moves the site.

   gsap.matchMedia undoes all of it, pages back in place, when the window changes layout or
   reduced motion is switched on. The theme editor keeps the still book: it re-renders the
   section on every change, and a still page lets it scroll a selected block into view.

   Shop the look runs first and needs no GSAP: a marker on a garment is a link whose label
   pops out of it. On a touch screen the first tap on a shut marker opens its label
   instead of leaving the page; a tap on an open one follows the link. A desktop book opens
   the labels of the spread it settles on by itself (`is-shown`); the still desktop layout
   does the same for a spread that stays on screen. */
(() => {
  const root = document.querySelector('[data-look]');
  if (!root) return;
  const book = root.querySelector('[data-book]');

  const tags = [...root.querySelectorAll('.m-look__tag')];
  if (tags.length) {
    let pointer = 'mouse';
    root.addEventListener('pointerdown', (e) => { pointer = e.pointerType; }, true);
    const labelShows = (tag) => Number(getComputedStyle(tag.querySelector('.m-look__tag-label')).opacity) > 0.5;
    root.addEventListener('click', (e) => {
      const tag = e.target.closest('.m-look__tag');
      if (pointer !== 'touch') return;
      tags.forEach((t) => { if (t !== tag) t.classList.remove('is-open'); });
      if (tag && !tag.classList.contains('is-open') && !labelShows(tag)) {
        e.preventDefault();
        tag.classList.add('is-open');
      }
    });
  }

  // A label opens toward the roomier side of the photo as the page crops it, measured in
  // the frame (an across half's plate is two frames wide). When neither side has room for
  // the whole label, it takes the roomier side and narrows, so the name wraps. The Liquid
  // sets the same classes from the photo's X and Y for a page without script.
  const place = () => tags.forEach((tag) => {
    const plate = tag.offsetParent;
    const frame = plate && plate.parentElement;
    const label = tag.querySelector('.m-look__tag-label');
    if (!frame || !frame.offsetWidth || !label) return;
    const rem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    const reach = (parseFloat(getComputedStyle(tag).getPropertyValue('--reach')) || 2.25) * rem; // --reach is set in rem
    const edge = frame.offsetWidth * 0.03;
    const cx = plate.offsetLeft + tag.offsetLeft + tag.offsetWidth / 2;
    const cy = plate.offsetTop + tag.offsetTop + tag.offsetHeight / 2;
    const right = frame.offsetWidth - edge - cx - reach;
    const left = cx - edge - reach;
    label.style.maxWidth = '';
    const width = label.offsetWidth;
    const goLeft = width > right && left > right;
    tag.classList.toggle('m-look__tag--left', goLeft);
    const room = goLeft ? left : right;
    if (width > room) label.style.maxWidth = `${Math.max(room, 96)}px`;
    tag.classList.toggle('m-look__tag--below', label.offsetHeight > cy - edge - reach);
  });
  if (tags.length) {
    place();
    if ('ResizeObserver' in window) {
      const ro = new ResizeObserver(place);
      new Set(tags.map((t) => t.offsetParent && t.offsetParent.parentElement)).forEach((f) => f && ro.observe(f));
    }
  }

  // The still desktop layout: a spread that stays on screen for a moment opens its labels.
  // The moving book marks its open pages itself, so the observer stands aside there.
  if (tags.length && 'IntersectionObserver' in window) {
    const timers = new Map();
    const io = new IntersectionObserver((entries) => entries.forEach(({ target, isIntersecting }) => {
      clearTimeout(timers.get(target));
      if (root.classList.contains('m-look--motion')) return;
      if (isIntersecting) timers.set(target, setTimeout(() => target.classList.add('is-shown'), 450));
      else target.classList.remove('is-shown');
    }), { threshold: 0.9 });
    new Set(tags.map((t) => t.closest('.m-look__spread'))).forEach((spread) => spread && io.observe(spread));
  }

  const { gsap, ScrollTrigger } = window;
  if (!book || !gsap || !ScrollTrigger || !window.matchMedia) return;
  if (window.Shopify && window.Shopify.designMode) return;

  gsap.registerPlugin(ScrollTrigger);
  ScrollTrigger.config({ ignoreMobileResize: true });

  const nav = root.querySelector('[data-look-nav]');
  const pages = [...book.querySelectorAll('.m-look__page')];
  // The fixed header's height, measured by melusio-header.js into --header-offset.
  const headerOffset = () => parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-offset')) || 0;

  // ---------- the contents and the page count ----------
  // Each contents link names a spread (#look-<handle>); its first page is where it leads.
  // Read now, while the pages still sit in their spreads.
  const count = root.querySelector('[data-look-count]');
  const total = book.dataset.pages || '';
  const entries = [...root.querySelectorAll('.m-look__entry')];
  const targets = new Map(entries.map((a) => [a, book.querySelector(`${a.getAttribute('href')} .m-look__page`)]));
  const folioOf = (page) => (page && page.querySelector('.m-look__folio')?.textContent.trim()) || '';
  // Shows the folios of the open pages between the arrows, and marks the drop they're in.
  function showOpen(open, at, indexOf) {
    const folios = open.map(folioOf).filter(Boolean);
    if (count) count.textContent = folios.length ? `${folios.join('–')} / ${total}` : '';
    let current = null;
    entries.forEach((a) => { if (targets.get(a) && indexOf(targets.get(a)) <= at) current = a; });
    entries.forEach((a) => a.classList.toggle('is-current', a === current));
  }
  // A contents link turns the moving book to its page instead of jumping to the anchor.
  function contents(go, signal) {
    entries.forEach((a) => a.addEventListener('click', (e) => {
      const page = targets.get(a);
      if (!page) return;
      e.preventDefault();
      go(page);
    }, { signal }));
  }

  // ---------- the world: the portal turning, Miuzy nodding, a note per page ----------
  const reader = root.querySelector('.m-look__reader');
  const parts = (name) => (reader ? [...reader.querySelectorAll(`[data-part="${name}"]`)] : []);
  const pivot = (part) => part.getAttribute('data-pivot') || undefined;
  function world() {
    [...root.querySelectorAll('.m-look__portal .m-portal__ring')].forEach((ring, i) => {
      gsap.to(ring, { rotation: i ? '-=360' : '+=360', duration: i ? 140 : 90, ease: 'none', repeat: -1, transformOrigin: '50% 50%' });
    });
    // Only the whole `sitter` group moves: his shoes, hands and head are separate shapes
    // that only meet by overlapping (see melusio-miuzy-sitting). A nod on the beat.
    parts('sitter').forEach((el) => gsap.fromTo(el, { rotation: 0, scaleY: 1, svgOrigin: pivot(el) },
      { rotation: 0.6, scaleY: 0.992, svgOrigin: pivot(el), duration: 0.34, ease: 'sine.inOut', yoyo: true, repeat: -1 }));
    const notes = parts('note');
    gsap.set(notes, { autoAlpha: 0 });
    let next = 0;
    // One note rises from his headphones and fades (SVG units; the art is ~1200 wide).
    return () => {
      const note = notes[next++ % (notes.length || 1)];
      if (!note) return;
      gsap.fromTo(note, { autoAlpha: 0, y: 40 }, {
        autoAlpha: 1, y: -30, duration: 0.8, ease: 'power2.out',
        onComplete: () => gsap.to(note, { autoAlpha: 0, y: -110, duration: 1.4, ease: 'power1.in' }),
      });
    };
  }

  // Moves the pages into leaves, each [front, back]; the returned `unbind` puts every page
  // back where the Liquid rendered it.
  function bind(mode, faces) {
    const home = pages.map((page) => [page, page.parentNode, page.nextSibling]);
    const holder = document.createElement('div');
    holder.className = 'm-look__leaves';
    const leaves = faces.map(([front, back]) => {
      const leaf = document.createElement('div');
      leaf.className = 'm-look__leaf';
      [[front, 'front'], [back, 'back']].forEach(([page, side]) => {
        if (!page) return;
        const face = document.createElement('div');
        face.className = `m-look__face m-look__face--${side}`;
        face.append(page);
        // The light on the paper as it turns: the page dims as it tilts away, and brightens
        // again as it lands (the timeline drives its opacity).
        const shade = document.createElement('span');
        shade.className = 'm-look__shade';
        face.append(shade);
        leaf.append(face);
      });
      holder.append(leaf);
      return leaf;
    });
    book.append(holder);
    root.classList.add('m-look--motion', `m-look--${mode}`);
    if (nav) nav.hidden = false;
    place();
    return {
      leaves,
      unbind() {
        home.slice().reverse().forEach(([page, parent, next]) => parent.insertBefore(page, next));
        holder.remove();
        root.classList.remove('m-look--motion', `m-look--${mode}`);
        if (nav) nav.hidden = true;
        pages.forEach((page) => page.classList.remove('is-shown'));
        place();
      },
    };
  }

  // Previous/next from the buttons and the arrow keys, in either mode.
  function controls(step, signal) {
    if (nav) {
      nav.addEventListener('click', (e) => {
        const button = e.target.closest('[data-turn]');
        if (button) step(Number(button.dataset.turn));
      }, { signal });
    }
    root.addEventListener('keydown', (e) => {
      if (e.target.closest('input, textarea, select')) return;
      if (e.key === 'ArrowRight') step(1);
      else if (e.key === 'ArrowLeft') step(-1);
    }, { signal });
  }
  const setButtons = (at, last) => {
    if (!nav) return;
    nav.querySelector('[data-turn="-1"]').disabled = at <= 0;
    nav.querySelector('[data-turn="1"]').disabled = at >= last;
  };

  const motion = '(prefers-reduced-motion: no-preference)';
  const mm = gsap.matchMedia();

  // ---------- desktop: an open book, turned by the scroll ----------
  mm.add(`${motion} and (min-width: 990px) and (orientation: landscape)`, () => {
    const [cover, ...pairs] = pages;
    const faces = [[cover, pairs[0]]];
    for (let j = 1; j < pairs.length; j += 2) faces.push([pairs[j], pairs[j + 1] || null]);
    const { leaves, unbind } = bind('spread', faces);
    const n = leaves.length;
    const turns = n - 1;
    const ac = new AbortController();

    // The pages open at spread s: the back of leaf s-1 and the front of leaf s.
    const openPages = (s) => [faces[s - 1] && faces[s - 1][1], faces[s] && faces[s][0]].filter(Boolean);
    const spreadOf = (page) => {
      const k = faces.findIndex(([front, back]) => front === page || back === page);
      return faces[k][0] === page ? k : k + 1;
    };

    leaves.forEach((leaf, i) => gsap.set(leaf, { zIndex: n - i, rotateY: 0 }));
    gsap.set(book, { xPercent: -25 });
    // The portal shows while the book lies closed on it and fades out as the cover opens:
    // left under the turning pages, it halved the frame rate. Its own opacity, not a
    // variable on the root: the pin resets the pinned root's inline style on refresh.
    const portal = root.querySelector('.m-look__portal');
    if (portal) gsap.set(portal, { opacity: 0.5 });
    const note = world();
    let noted = 0;

    let settleTimer;
    const settle = (at) => {
      clearTimeout(settleTimer);
      const s = Math.round(at);
      setButtons(s, turns);
      if (Math.abs(at - s) > 0.01) {
        pages.forEach((page) => page.classList.remove('is-shown'));
        return;
      }
      settleTimer = setTimeout(() => {
        const open = openPages(s);
        pages.forEach((page) => page.classList.toggle('is-shown', open.includes(page)));
        showOpen(open, s, spreadOf);
        if (s !== noted && s > 0) note();
        noted = s;
      }, 350);
    };

    const tl = gsap.timeline({
      defaults: { ease: 'power1.inOut' },
      scrollTrigger: {
        trigger: root,
        pin: true,
        start: () => `top ${headerOffset()}px`,
        // Paced so a turn reads as a page being turned, not a flick (slowed after feedback,
        // 2026-09-25): 1.4 screens of scrolling per page, the book easing a second behind
        // the scroll, and a snap that finishes a turn in 0.7 to 1.3 seconds.
        end: () => `+=${turns * window.innerHeight * 1.4}`,
        scrub: 1,
        // No inertia: a fast flick would otherwise be projected past several pages.
        snap: { snapTo: 1 / turns, inertia: false, duration: { min: 0.7, max: 1.3 }, ease: 'power1.inOut' },
        invalidateOnRefresh: true,
        onUpdate: (self) => {
          const at = self.progress * turns;
          // The stack beside each page: none on the left until the cover has landed there.
          book.style.setProperty('--stack-l', `${at < 1 ? 0 : 1 + ((at - 1) / Math.max(1, turns - 1)) * 7}px`);
          book.style.setProperty('--stack-r', `${1 + ((turns - at) / turns) * 7}px`);
          settle(at);
        },
      },
    });
    tl.to(book, { xPercent: 0, duration: 1 }, 0);
    if (portal) tl.to(portal, { autoAlpha: 0, duration: 1 }, 0);
    leaves.slice(0, turns).forEach((leaf, i) => {
      tl.to(leaf, { rotateY: -180, duration: 1 }, i);
      // Past the spine it lies on the turned pages, so it must cover them.
      tl.set(leaf, { zIndex: n + i }, i + 0.5);
      const [front, back] = [...leaf.querySelectorAll('.m-look__shade')];
      tl.fromTo(front, { opacity: 0 }, { opacity: 0.22, duration: 0.5, ease: 'power1.in' }, i);
      if (back) tl.fromTo(back, { opacity: 0.22 }, { opacity: 0, duration: 0.5, ease: 'power1.out' }, i + 0.5);
    });

    const goTo = (s, behavior) => {
      const st = tl.scrollTrigger;
      const target = Math.max(0, Math.min(turns, s));
      window.scrollTo({ top: st.start + (target / turns) * (st.end - st.start), behavior });
    };
    const at = () => Math.round(tl.scrollTrigger.progress * turns);
    controls((dir) => goTo(at() + dir, 'smooth'), ac.signal);
    contents((page) => goTo(spreadOf(page), 'smooth'), ac.signal);
    // A keyboard visitor tabbing to a link on a page that isn't open: open the book there.
    root.addEventListener('focusin', (e) => {
      const page = e.target.closest('.m-look__page');
      if (page && !openPages(at()).includes(page)) goTo(spreadOf(page), 'instant');
    }, { signal: ac.signal });
    settle(0);

    return () => {
      ac.abort();
      clearTimeout(settleTimer);
      unbind();
      gsap.set(book, { clearProps: 'transform' });
      if (portal) gsap.set(portal, { clearProps: 'opacity,visibility' });
      if (count) count.textContent = '';
      book.style.removeProperty('--stack-l');
      book.style.removeProperty('--stack-r');
    };
  });

  // ---------- phone or portrait: one page, turned by a swipe ----------
  const single = () => {
    const list = pages.filter((page) => !page.classList.contains('m-look__page--blank'));
    const { leaves, unbind } = bind('single', list.map((page) => [page, null]));
    const n = leaves.length;
    const ac = new AbortController();
    let at = 0;

    leaves.forEach((leaf, i) => gsap.set(leaf, { zIndex: n - i, rotateY: 0, visibility: 'visible' }));
    world();
    const shown = () => showOpen([list[at]], at, (page) => list.indexOf(page));
    const shadeOf = (leaf) => leaf.querySelector('.m-look__shade');
    const turnTo = (to) => {
      const target = Math.max(0, Math.min(n - 1, to));
      if (target === at) return;
      if (target > at) {
        leaves.slice(at, target).forEach((leaf) => gsap.to(leaf, {
          rotateY: -180, duration: 1.1, ease: 'power2.inOut',
          // It dims as it tilts away from the light, like the desktop book's pages.
          onStart: () => gsap.fromTo(shadeOf(leaf), { opacity: 0 }, { opacity: 0.22, duration: 0.55, ease: 'power1.in' }),
          // Turned away it shows only its hidden back; keep it out of taps and focus.
          onComplete: () => gsap.set(leaf, { visibility: 'hidden' }),
        }));
      } else {
        leaves.slice(target, at).forEach((leaf) => {
          gsap.set(leaf, { visibility: 'visible' });
          gsap.to(leaf, { rotateY: 0, duration: 1.1, ease: 'power2.inOut' });
          gsap.fromTo(shadeOf(leaf), { opacity: 0.22 }, { opacity: 0, duration: 1.1, ease: 'power1.out' });
        });
      }
      at = target;
      setButtons(at, n - 1);
      shown();
    };
    const jumpTo = (to) => {
      leaves.forEach((leaf, i) => gsap.set(leaf, { rotateY: i < to ? -180 : 0, visibility: i < to ? 'hidden' : 'visible' }));
      at = to;
      setButtons(at, n - 1);
      shown();
    };

    controls((dir) => turnTo(at + dir), ac.signal);
    contents((page) => turnTo(list.indexOf(page)), ac.signal);

    // A horizontal swipe turns the page; a vertical one scrolls the site (touch-action:
    // pan-y). A swipe that starts on a marker must not also count as a tap on it.
    let startX = 0;
    let startY = 0;
    let swiped = false;
    book.addEventListener('pointerdown', (e) => { startX = e.clientX; startY = e.clientY; swiped = false; }, { signal: ac.signal });
    book.addEventListener('pointerup', (e) => {
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
        swiped = true;
        turnTo(at + (dx < 0 ? 1 : -1));
      }
    }, { signal: ac.signal });
    book.addEventListener('click', (e) => {
      if (!swiped) return;
      swiped = false;
      e.preventDefault();
      e.stopPropagation();
    }, { signal: ac.signal, capture: true });

    root.addEventListener('focusin', (e) => {
      const page = e.target.closest('.m-look__page');
      const i = list.indexOf(page);
      if (i >= 0 && i !== at) jumpTo(i);
    }, { signal: ac.signal });
    setButtons(0, n - 1);
    shown();

    return () => {
      ac.abort();
      unbind();
      if (count) count.textContent = '';
    };
  };
  mm.add(`${motion} and (max-width: 989px)`, single);
  mm.add(`${motion} and (min-width: 990px) and (orientation: portrait)`, single);
})();
